"use client";

import * as React from "react";
import { Command as CommandPrimitive } from "cmdk";
import { Loader2 } from "lucide-react";

import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

interface Artist {
  id: number;
  artist: string;
}

interface ArtistSearchProps {
  onArtistSelect: (artistData: { artist: string }) => void;
  initialValue?: string;
}

export default function ArtistSearch({
  onArtistSelect,
  initialValue,
}: ArtistSearchProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(initialValue || "");
  const [artists, setArtists] = React.useState<Artist[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Track if the input should have focus
  const shouldMaintainFocus = React.useRef(false);

  React.useEffect(() => {
    if (initialValue) {
      setInputValue(initialValue);
      onArtistSelect({ artist: initialValue });
    }
  }, [initialValue, onArtistSelect]);

  // Effect to maintain focus when needed
  React.useEffect(() => {
    if (shouldMaintainFocus.current && inputRef.current && !isLoading) {
      inputRef.current.focus();
      shouldMaintainFocus.current = false;
    }
  }, [isLoading, artists]);

  React.useEffect(() => {
    const fetchArtists = async () => {
      if (inputValue.length > 1) {
        // Set flag to maintain focus after API call completes
        shouldMaintainFocus.current =
          document.activeElement === inputRef.current;

        setIsLoading(true);
        setError(null);
        try {
          const response = await fetch(
            `/api/artists/autocomplete?query=${encodeURIComponent(inputValue)}`,
          );
          if (response.ok) {
            const data = await response.json();
            setArtists(data);
          } else {
            setError("Failed to load artists");
            setArtists([]);
          }
        } catch (error) {
          console.error("Error fetching artists:", error);
          setError("Error loading artists");
          setArtists([]);
        } finally {
          setIsLoading(false);
        }
      } else {
        setArtists([]);
      }
    };

    const timeoutId = setTimeout(fetchArtists, 300);
    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current;
      if (input) {
        if (e.key === "Escape") {
          input.blur();
        }
      }
    },
    [],
  );

  const handleArtistSelect = React.useCallback(
    (artist: Artist) => {
      onArtistSelect({
        artist: artist.artist,
      });
      setInputValue(artist.artist);
      setOpen(false);
    },
    [onArtistSelect],
  );

  return (
    <Command
      onKeyDown={handleKeyDown}
      className="h-fit overflow-visible bg-transparent"
    >
      <div className="flex h-9 w-full rounded-md border border-zinc-200 bg-transparent px-3 py-1 text-sm shadow-xs transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-zinc-500 focus-visible:ring-1 focus-visible:ring-zinc-950 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-800 dark:placeholder:text-zinc-400 dark:focus-visible:ring-zinc-300">
        <div className="relative flex w-full items-center">
          <CommandPrimitive.Input
            ref={inputRef}
            value={inputValue}
            onValueChange={(value) => {
              setInputValue(value);
              setOpen(true);
              onArtistSelect({ artist: value });
            }}
            onBlur={() => {
              setTimeout(() => setOpen(false), 200);
            }}
            onFocus={() => setOpen(true)}
            placeholder="Search for an artist..."
            className="placeholder:text-muted-foreground w-full flex-1 bg-transparent pr-6 outline-hidden"
          />
          {isLoading && (
            <div className="absolute right-0 flex items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />
            </div>
          )}
        </div>
      </div>
      <div className="relative">
        <CommandList>
          {open && (artists.length > 0 || error || isLoading) ? (
            <div className="bg-popover text-popover-foreground animate-in absolute top-0 z-10 w-full rounded-md border shadow-md outline-hidden">
              <CommandGroup className="overflow-auto">
                {error ? (
                  <div className="px-2 py-3 text-sm text-red-500">{error}</div>
                ) : isLoading ? (
                  <div className="flex items-center justify-center py-3">
                    <span className="text-sm text-zinc-500">
                      Loading artists...
                    </span>
                  </div>
                ) : (
                  artists.map((artist) => (
                    <CommandItem
                      key={artist.id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onSelect={() => handleArtistSelect(artist)}
                      className="cursor-pointer"
                    >
                      {artist.artist}
                    </CommandItem>
                  ))
                )}
              </CommandGroup>
            </div>
          ) : null}
        </CommandList>
      </div>
    </Command>
  );
}
